import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

// Per-group narrative for the first two onboarding steps. Steps 3 and 4 are
// generated dynamically from the user's nav_keys (flashcards/quizzes/certs),
// so any group automatically gets the right onboarding path.
const GROUP_OVERVIEWS = {
    "appointment-setters": {
        kicker: "Appointment Setters · Onboarding",
        intro: "A short orientation for setters running HAEVN outreach. Take it in order, take it at your pace.",
        step1: { title: "Understand HAEVN", body: "Begin with HAEVN Fundamentals to ground yourself in the brand, the mission, and the language we use with prospects." },
        step2: { title: "Master the program", body: "Move through the Meetup Layer and Venue Fit modules to learn what we sell and why operators say yes." },
        closing: "Mastery is the goal, not speed. Repeat the sections that feel uncertain — the work compounds.",
    },
    "marketing-partners": {
        kicker: "Marketing Partners · Onboarding",
        intro: "A short orientation for agency partners representing HAEVN. Read the brand, talk the brand.",
        step1: { title: "Learn the brand", body: "Start with HAEVN Brand Foundations to internalize positioning, voice, and how we frame the product." },
        step2: { title: "Walk the product", body: "Move through the HAEVN Product Walkthrough so you can speak to the experience credibly." },
        closing: "How you talk about HAEVN is how the market hears us. Slow down on the language, get it right.",
    },
    "ugc-creators": {
        kicker: "UGC Creators · Onboarding",
        intro: "A short orientation for creators producing HAEVN-aligned content. Anchor in the brand before you film.",
        step1: { title: "Internalize the brand", body: "Start with HAEVN Brand Foundations to understand the positioning, the voice, and what we never say." },
        step2: { title: "Watch the product", body: "Walk through the HAEVN Product Walkthrough so your content reflects the real experience." },
        closing: "Authentic content beats polished content. Lead with truth, not hype.",
    },
    "haevn-101": {
        kicker: "HAEVN 101 · Orientation",
        intro: "A short orientation to HAEVN: what we are, how we talk about it, and how the product actually works.",
        step1: { title: "What HAEVN is", body: "Begin with HAEVN Brand Foundations to ground yourself in the brand and the positioning." },
        step2: { title: "How HAEVN works", body: "Walk through the HAEVN Product Walkthrough to see the experience end to end." },
        closing: "This is the shared baseline. Everything else builds on it.",
    },
};

const GENERIC_OVERVIEW = {
    kicker: "Onboarding",
    intro: "A short orientation to help you move through HAEVN University with intent. Take it in order, take it at your pace.",
    step1: { title: "Get oriented", body: "Start with your assigned modules to ground yourself in the material." },
    step2: { title: "Move through the curriculum", body: "Work through each module in order. Every lesson builds on the last." },
    closing: "Speed is not the goal. Mastery is. Repeat the sections that feel uncertain — the work compounds.",
};

export default function StartHere() {
    const { user, navVersion } = useAuth();
    const [navFlags, setNavFlags] = useState(null);

    useEffect(() => {
        api.get("/me/nav").then((r) => setNavFlags(r.data)).catch(() => {});
    }, [user?.id, user?.preview_groups?.length, navVersion]);

    // Effective group context — preview wins for admins.
    const effectiveGroups = useMemo(() => {
        const preview = user?.preview_groups || [];
        if (preview.length > 0) return preview;
        return user?.groups || [];
    }, [user?.groups, user?.preview_groups]);

    // Pick narrative: if the user is in exactly one of the well-known groups,
    // use its tailored copy. Otherwise (multiple groups, custom group, or
    // staff with no group), fall back to generic steps.
    const overview = useMemo(() => {
        if (effectiveGroups.length === 1) {
            const slug = effectiveGroups[0].slug;
            return GROUP_OVERVIEWS[slug] || GENERIC_OVERVIEW;
        }
        return GENERIC_OVERVIEW;
    }, [effectiveGroups]);

    const groupLabel = effectiveGroups.length === 0
        ? null
        : effectiveGroups.map((g) => g.title).join(" + ");

    const navKeys = navFlags?.nav_keys || [];
    const hasFlashcards = navKeys.includes("flashcards") && navFlags?.flashcards !== false;
    const hasQuizzes = navKeys.includes("quizzes") && navFlags?.quizzes !== false;
    const hasCertifications = navKeys.includes("certifications");

    // Build steps dynamically. Steps 3 and 4 only render if their feature is
    // available to this group.
    const steps = [];
    steps.push({ n: "01", title: overview.step1.title, body: overview.step1.body, to: "/modules", testId: "start-step-1" });
    steps.push({ n: "02", title: overview.step2.title, body: overview.step2.body, to: "/modules", testId: "start-step-2" });
    if (hasFlashcards || hasQuizzes) {
        const targets = [];
        if (hasFlashcards) targets.push("flash cards");
        if (hasQuizzes) targets.push("quizzes");
        const target = targets.join(" and ");
        steps.push({
            n: String(steps.length + 1).padStart(2, "0"),
            title: "Build muscle memory",
            body: `Use ${target} to test recall and sharpen the language. Repetition is how the material sticks.`,
            to: hasFlashcards ? "/flashcards" : "/quizzes",
            testId: "start-step-3",
        });
    }
    if (hasCertifications) {
        steps.push({
            n: String(steps.length + 1).padStart(2, "0"),
            title: "Earn certification",
            body: "Complete every required module and pass each quiz at threshold to earn your HAEVN certification.",
            to: "/certifications",
            testId: "start-step-4",
        });
    }

    return (
        <div className="max-w-4xl space-y-12" data-testid="start-here-page">
            <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-ink-400 font-mono mb-3" data-testid="start-here-kicker">
                    {overview.kicker}
                </div>
                <h1 className="font-editorial text-4xl md:text-5xl text-ink-900 leading-[1.05]">
                    Start here.
                </h1>
                <p className="mt-3 text-base text-ink-500 max-w-2xl" data-testid="start-here-intro">
                    {overview.intro}
                </p>
                {groupLabel && (
                    <div className="mt-4 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] font-mono text-ink-400">
                        <span className="w-1.5 h-1.5 rounded-full bg-haevn-700" />
                        You are in: <span className="text-ink-900">{groupLabel}</span>
                    </div>
                )}
            </div>

            <ol className="space-y-px bg-ink-300 border border-ink-300">
                {steps.map((s) => (
                    <li key={s.n} className="bg-white p-8 grid md:grid-cols-[100px_1fr_auto] gap-6 items-start">
                        <div className="font-mono text-2xl text-ink-300">{s.n}</div>
                        <div>
                            <h3 className="font-heading text-xl text-ink-900">{s.title}</h3>
                            <p className="text-sm text-ink-500 mt-1 max-w-xl">{s.body}</p>
                        </div>
                        <Link
                            to={s.to}
                            data-testid={s.testId}
                            className="text-xs font-mono uppercase tracking-[0.15em] text-haevn-700 hover:text-haevn-900 self-end md:self-center"
                        >
                            Begin →
                        </Link>
                    </li>
                ))}
            </ol>

            <div className="bg-haevn-700 text-white p-10 grain relative">
                <div className="text-[10px] uppercase tracking-[0.25em] opacity-70 font-mono mb-3">A note on pace</div>
                <p className="font-editorial text-2xl leading-snug max-w-2xl" data-testid="start-here-closing">
                    {overview.closing}
                </p>
            </div>
        </div>
    );
}
